import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Wrench, Upload, Loader2, AlertTriangle, CheckCircle, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { DEFAULT_ISSUE_CATEGORY, ISSUE_CATEGORIES, ISSUE_CATEGORY_OPTIONS } from '../lib/issueCategories';
import { CATEGORY_ICONS } from '../lib/issueCategoryIcons';
import { getIssueDescriptionTemplate } from '../lib/issueFormHelpers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ReportIssue = () => {
  useEffect(() => { document.title = 'Report an Issue - SIYAK'; }, []);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [form, setForm] = useState({
    description: '',
    category: DEFAULT_ISSUE_CATEGORY,
    sub_category: '',
    custom_title: '',
    priority: 'medium',
    photos: []
  });

  const subCategoryOptions = ISSUE_CATEGORIES[form.category] || [];
  const SelectedCategoryIcon = CATEGORY_ICONS[form.category];

  const updateIssueDetails = (updates) => {
    setForm((current) => {
      const currentTemplate = getIssueDescriptionTemplate(current);
      const next = { ...current, ...updates };
      const nextTemplate = getIssueDescriptionTemplate(next);

      if (!current.description.trim() || current.description === currentTemplate) {
        return {
          ...next,
          description: nextTemplate,
        };
      }

      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const data = {
        description: form.description,
        category: form.category,
        sub_category: form.sub_category || null,
        custom_title: form.category === 'Other' ? (form.custom_title.trim() || null) : null,
        priority: form.priority,
        photos: form.photos
        // property_id will be auto-assigned from user's assigned property in backend
      };
      
      await axios.post(`${API_URL}/api/issues`, data);
      setSubmitted(true);
      toast.success('Issue reported successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to report issue');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      toast.info('Photo upload noted. In production, photos would be uploaded to storage.');
      setForm({
        ...form,
        photos: [...form.photos, ...files.map(f => `uploaded:${f.name}`)]
      });
    }
  };

  if (submitted) {
    return (
      <Layout>
        <div className="bg-navy-50 min-h-screen">
          <div className="max-w-2xl mx-auto px-4 py-16 text-center">
            <div className="bg-white rounded-lg p-12 shadow-sm">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-6">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-navy-900 mb-4">
                Issue Reported Successfully
              </h1>
              <p className="text-muted-foreground mb-8">
                We've received your maintenance request and will address it as soon as possible. 
                You can track the status in your issues dashboard.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button onClick={() => navigate('/tenant')}>
                  Go to My Tenancy
                </Button>
                <Button variant="outline" onClick={() => {
                  setSubmitted(false);
                  setForm({
                    description: '',
                    category: DEFAULT_ISSUE_CATEGORY,
                    sub_category: '',
                    custom_title: '',
                    priority: 'medium',
                    photos: []
                  });
                }}>
                  Report Another Issue
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="bg-navy-50 min-h-screen" data-testid="report-issue-page">
        {/* Header */}
        <div className="bg-white border-b">
          <div className="max-w-3xl mx-auto px-4 md:px-8 py-8">
            <div className="flex items-center gap-3 mb-2">
              <Wrench className="h-8 w-8 text-royal-600" />
              <h1 className="font-heading text-3xl font-bold text-navy-900">
                Report an Issue
              </h1>
            </div>
            <p className="text-muted-foreground">
              Let us know about any maintenance issues or problems in your property
            </p>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-4 md:px-8 py-8">
          {/* Show assigned property info */}
          {user?.property_title && (
            <Card className="mb-8 bg-royal-50 border-royal-200">
              <CardContent className="p-4 flex items-center gap-3">
                <Building2 className="h-5 w-5 text-royal-600" />
                <div>
                  <p className="font-medium text-royal-900">Your Property</p>
                  <p className="text-sm text-royal-700">{user.property_title}</p>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Issue Details</CardTitle>
              <CardDescription>
                Please provide as much detail as possible to help us resolve the issue quickly.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Priority */}
                <div className="space-y-2">
                  <Label>Category *</Label>
                  <Select
                    value={form.category}
                    onValueChange={(value) => updateIssueDetails({ category: value, sub_category: '', custom_title: '' })}
                  >
                    <SelectTrigger data-testid="category-select">
                      <SelectValue asChild>
                        <div className="flex items-center gap-2">
                          {SelectedCategoryIcon ? <SelectedCategoryIcon size={16} /> : null}
                          <span>{form.category}</span>
                        </div>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {ISSUE_CATEGORY_OPTIONS.map((category) => {
                        const Icon = CATEGORY_ICONS[category];
                        return (
                          <SelectItem key={category} value={category}>
                            <div className="flex items-center gap-2">
                              {Icon ? <Icon size={16} /> : null}
                              <span>{category}</span>
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                {subCategoryOptions.length > 0 && (
                  <div className="space-y-2">
                    <Label>Subcategory (Optional)</Label>
                    <Select
                      value={form.sub_category || '__none__'}
                      onValueChange={(value) => updateIssueDetails({ sub_category: value === '__none__' ? '' : value })}
                    >
                      <SelectTrigger data-testid="sub-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">None</SelectItem>
                        {subCategoryOptions.map((subCategory) => (
                          <SelectItem key={subCategory} value={subCategory}>{subCategory}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {form.category === 'Other' && (
                  <div className="space-y-2">
                    <Label htmlFor="custom_title">Custom Title *</Label>
                    <Input
                      id="custom_title"
                      required
                      placeholder="Enter a short custom issue title"
                      value={form.custom_title}
                      onChange={(e) => updateIssueDetails({ custom_title: e.target.value })}
                      data-testid="custom-title-input"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Priority Level</Label>
                  <Select 
                    value={form.priority} 
                    onValueChange={(val) => setForm({...form, priority: val})}
                  >
                    <SelectTrigger data-testid="priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low - Minor inconvenience</SelectItem>
                      <SelectItem value="medium">Medium - Needs attention soon</SelectItem>
                      <SelectItem value="high">High - Urgent repair needed</SelectItem>
                      <SelectItem value="urgent">Urgent - Emergency situation</SelectItem>
                    </SelectContent>
                  </Select>
                  {form.priority === 'urgent' && (
                    <div className="flex items-center gap-3 p-4 bg-red-50 rounded-md text-red-700 text-sm">
                      <AlertTriangle className="h-4 w-4" />
                      For emergencies (gas leak, flood, etc.), please also call us immediately.
                    </div>
                  )}
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    required
                    placeholder="Please describe the issue in detail. Include information like:
- Where exactly is the problem?
- When did you first notice it?
- Has it gotten worse over time?
- Any other relevant details..."
                    value={form.description}
                    onChange={(e) => setForm({...form, description: e.target.value})}
                    rows={6}
                    className="min-h-[130px]"
                    data-testid="issue-description-input"
                  />
                </div>

                {/* Photo Upload */}
                <div className="space-y-2">
                  <Label>Photos (Optional)</Label>
                  <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center">
                    <Upload className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground mb-2">
                      Upload photos to help us understand the issue better
                    </p>
                    <Input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="max-w-xs mx-auto"
                      data-testid="photo-upload-input"
                    />
                  </div>
                  {form.photos.length > 0 && (
                    <p className="text-sm text-muted-foreground">
                      {form.photos.length} photo(s) selected
                    </p>
                  )}
                </div>

                {/* Submit */}
                <div className="pt-6">
                  <Button
                    type="submit"
                    size="lg" 
                    className="w-full"
                    disabled={submitting}
                    data-testid="submit-issue-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Wrench className="h-4 w-4 mr-2" />
                        Submit Issue Report
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};
